/**
 * Downscale a captured photo before it goes to the evidence bucket. Phone
 * cameras produce 3 to 12 MB originals; the vision model only needs ~1280px on
 * the long edge, and gym Wi-Fi at close is the slowest link in the chain.
 */

/** Longest edge capped at maxEdge, aspect preserved; smaller images untouched. */
export const fitWithin = (width, height, maxEdge) => {
    const w = Number(width);
    const h = Number(height);
    const cap = Number(maxEdge);
    if (!(w > 0) || !(h > 0)) return { width: 0, height: 0 };
    const longest = Math.max(w, h);
    if (!(cap > 0) || longest <= cap) return { width: Math.round(w), height: Math.round(h) };
    const scale = cap / longest;
    return {
        width: Math.max(1, Math.round(w * scale)),
        height: Math.max(1, Math.round(h * scale)),
    };
};

const decodeBitmap = async (file) => {
    // 'from-image' applies the EXIF rotation phones store instead of rotating
    // pixels; older engines reject the options bag, so retry bare.
    try {
        return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (e) {
        return createImageBitmap(file);
    }
};

/**
 * Promise<Blob> of a JPEG no larger than maxEdge on its long side. Falls back
 * to the original file when the browser cannot decode or draw it (no
 * createImageBitmap / canvas, an unsupported format); the upload still goes
 * through and the server judges what it gets.
 */
export const resizeImageForUpload = async (file, maxEdge = 1280, quality = 0.8) => {
    if (!file || typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;
    let bitmap = null;
    try {
        bitmap = await decodeBitmap(file);
        const { width, height } = fitWithin(bitmap.width, bitmap.height, maxEdge);
        if (!width || !height) return file;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        if (!context || typeof canvas.toBlob !== 'function') return file;
        context.drawImage(bitmap, 0, 0, width, height);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
        return blob || file;
    } catch (e) {
        return file;
    } finally {
        if (bitmap && typeof bitmap.close === 'function') bitmap.close();
    }
};
